<!--
  @component Card — glass-material section container.
  tier:      primitive (visual atom)
  purpose:   the default surface for screen sections, so screens stop
             hand-rolling box CSS. Two elevation tiers from the Slate
             Ink system: tier 1 (frosted section) and tier 2 (raised
             focal panel + shadow).

  Slots:
    actions — header-right controls (snippet)
    children — body content
-->
<script lang="ts" module>
  import type { Snippet } from "svelte";

  export interface CardProps {
    /** Uppercase eyebrow label above the title. */
    eyebrow?: string;
    /** Section title. */
    title?: string;
    /** Tier 2 — raised focal panel with shadow. Default tier 1. */
    elevated?: boolean;
    /** Apply body padding. Default true. */
    pad?: boolean;
    actions?: Snippet;
    children: Snippet;
  }
</script>

<script lang="ts">
  let {
    eyebrow,
    title,
    elevated = false,
    pad = true,
    actions,
    children,
  }: CardProps = $props();

  const hasHeader = $derived(Boolean(eyebrow || title || actions));
</script>

<section class="card" class:elevated>
  {#if hasHeader}
    <header class="card-head">
      <div class="titles">
        {#if eyebrow}<p class="eyebrow">{eyebrow}</p>{/if}
        {#if title}<h2 class="title">{title}</h2>{/if}
      </div>
      {#if actions}<div class="actions">{@render actions()}</div>{/if}
    </header>
  {/if}
  <div class="body" class:pad>
    {@render children()}
  </div>
</section>

<style>
  .card {
    background: var(--glass-fill);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-lg);
    font-family: var(--type-font);
    overflow: hidden;
  }
  .card.elevated {
    background: var(--surface-raised);
    box-shadow: var(--shadow-md);
  }
  .card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--space-4);
    padding: var(--space-4) var(--space-5);
    border-bottom: 0.5px solid var(--border-subtle);
  }
  .titles { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .eyebrow {
    margin: 0;
    font-size: 10.5px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text-tertiary);
  }
  .title {
    margin: 0;
    font-size: 14.5px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--text-primary);
  }
  .actions { display: flex; align-items: center; gap: var(--space-2); flex-shrink: 0; }
  .body.pad { padding: var(--space-5); }
</style>
